import { useEffect } from 'react';
import { useT } from '../lib/i18n';
import {
  ageBandFor, partyCategoriesFor, partySize, partyWithin,
  type CardShape, type Party,
} from '../lib/party';
import { Stepper } from './Stepper';

/**
 * Who is coming.
 *
 * The categories are channel roles rather than the tariff card's own quantity
 * keys, and `lib/party.ts` explains at length why that is a compromise and what
 * would remove it. Nothing about that shows here: a guest counts adults and
 * children, which is what they know.
 */
export function PartyPicker({ party, onChange, labelledBy, accommodations }: {
  party: Party;
  onChange: (party: Party) => void;
  labelledBy?: string;
  /**
   * The park's cards, for the age behind each category.
   *
   * The subtitles used to be sentences about the mechanism. A guest wants
   * "18+" and "3\u201317 jaar", and the park already states exactly that on
   * every guest category — see `ageBandFor` for why it is read from there
   * rather than typed a second time.
   */
  accommodations?: ReadonlyArray<CardShape & { quantities?: ReadonlyArray<{
    channel?: string; key: string; ageFrom?: number; ageUntil?: number;
  }> }>;
}) {
  const t = useT();

  /*
    Which categories this park will actually take, rather than all four.

    `partyCategoriesFor` carries the reasoning: an animal category is drawn
    only where a card declares one, and capped where the operator has capped
    it. On this park's rental units that means no dog control at all, which is
    the honest answer — those cards price per unit and say nothing about
    animals.
  */
  const categories = partyCategoriesFor(accommodations);

  /*
    A dog the guest can no longer see must not still be in the party.

    Narrowing a camping search to a rental unit takes the control away; without
    this the count stays behind, every card refuses a party it cannot price,
    and the guest reads "ask us for a price" with nothing on screen to explain
    why. Same for a deep link that arrives carrying one.

    Reported upward rather than filtered on the way out, so the party the guest
    can see and the party being priced are the same object.
  */
  const pruned = partyWithin(party, categories);
  const drifted = JSON.stringify(pruned) !== JSON.stringify(party);
  useEffect(() => { if (drifted) onChange(pruned); });

  const subtitle = (role: string): string | null => {
    const band = ageBandFor(role, accommodations);
    if (!band) return null;
    if (band.from !== undefined && band.until !== undefined) {
      return t('party.ageBetween', { from: band.from, until: band.until });
    }
    if (band.from !== undefined) return t('party.ageFrom', { age: band.from });
    if (band.until !== undefined) return t('party.ageUnder', { age: band.until });
    return null;
  };
  return (
    <div className="pw-counters" role="group" aria-labelledby={labelledBy}>
      {categories.map((cat) => {
        const label = t(cat.label);
        const hintId = `pw-party-${cat.key}`;
        const value = party[cat.key] ?? cat.min;
        /*
          A category that can only be there or not is a yes/no question.

          REPORTED: "dog is 0/1, so it can be a checkbox". A stepper for it is
          two buttons of which one is always disabled, and the operator read
          that as permission to add a second dog. A tick has one state to be
          in and cannot be pressed into an illegal one.
        */
        const yesNo = cat.min === 0 && cat.max === 1;
        return (
          <div className="pw-counter" key={cat.key}>
            <div className="pw-counter-text">
              <div className="pw-counter-name" id={hintId}>{label}</div>
              {/* The park's own age band, or nothing. A sentence that says
                  less than silence is worse than silence. */}
              {subtitle(cat.key) && (
                <div className="pw-counter-hint">{subtitle(cat.key)}</div>
              )}
            </div>
            {yesNo ? (
              // The label wraps the box, so the whole row is the tap target
              // rather than a 20px square. The name is already on screen in
              // `pw-counter-name`, so this one is for a screen reader only.
              <label className="pw-check">
                <input
                  type="checkbox"
                  checked={value > 0}
                  aria-label={label}
                  aria-describedby={hintId}
                  onChange={(e) => onChange({ ...party, [cat.key]: e.target.checked ? 1 : 0 })}
                />
              </label>
            ) : (
              <Stepper
                value={value}
                min={cat.min}
                max={cat.max}
                name={label}
                describedBy={hintId}
                onChange={(next) => onChange({ ...party, [cat.key]: next })}
              />
            )}
          </div>
        );
      })}
      <p className="pw-note pw-num">
        {t('party.summary', { count: partySize(pruned) })}
      </p>
    </div>
  );
}
